import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { useCart } from "../contexts/CartContext";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import api from "../lib/api";
import { sanitizeUrlInput } from "../lib/urlUtils";
import { toast } from "sonner";
import { ShoppingCart, Trash2, Plus, X, HelpCircle } from "lucide-react";

const FloatingCart = () => {
  const { cart, cartOpen, setCartOpen, removeFromCart, removeMultipleFromCart, setAllAnchorText, clearCart, getCartTotal } = useCart();
  const { refreshUser, user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const hasActiveSubscription = Boolean(
    user &&
    (["admin", "super_admin"].includes(user.role) ||
      (user.is_subscribed &&
        user.subscription_end_date &&
        new Date(user.subscription_end_date.replace("Z", "+00:00")) > new Date()))
  );
  let subscriptionEndLabel = "";
  let subscriptionDaysLeft = 0;
  if (
    hasActiveSubscription &&
    user?.subscription_end_date &&
    !["admin", "super_admin"].includes(user.role)
  ) {
    const end = new Date(user.subscription_end_date.replace("Z", "+00:00"));
    subscriptionEndLabel = end.toLocaleDateString(language === "tr" ? "tr-TR" : "en-US");
    subscriptionDaysLeft = Math.max(0, Math.ceil((end.getTime() - Date.now()) / (1000 * 60 * 60 * 24)));
  } else if (hasActiveSubscription && ["admin", "super_admin"].includes(user?.role)) {
    subscriptionEndLabel = "";
    subscriptionDaysLeft = 0;
  }
  
  const [targetUrl, setTargetUrl] = useState(() =>
    sanitizeUrlInput(localStorage.getItem("rootseo_target_url") || "")
  );
  const [bulkOrdering, setBulkOrdering] = useState(false);
  // Hedef URL başına en fazla 3 keyword (tüm sitelere uygulanır)
  const [keyword1, setKeyword1] = useState("");
  const [keyword2, setKeyword2] = useState("");
  const [keyword3, setKeyword3] = useState("");
  const [showKeyword2, setShowKeyword2] = useState(false);
  const [showKeyword3, setShowKeyword3] = useState(false);
  const [hiddenLink, setHiddenLink] = useState(false);

  const resetKeywords = () => {
    setKeyword1("");
    setKeyword2("");
    setKeyword3("");
    setShowKeyword2(false);
    setShowKeyword3(false);
    setHiddenLink(false);
  };

  const handleOpenCart = () => {
    setCartOpen(true);
  };

  const handleBulkOrder = async () => {
    // Validation
    const cleanTarget = sanitizeUrlInput(targetUrl);
    if (!cleanTarget) {
      toast.error(language === "tr" ? "Hedef URL gerekli" : "Target URL required");
      return;
    }
    const kws = [keyword1.trim(), showKeyword2 ? keyword2.trim() : "", showKeyword3 ? keyword3.trim() : ""].filter(Boolean);
    if (!keyword1.trim()) {
      toast.error(language === "tr" ? "1. keyword zorunludur" : "1st keyword is required");
      return;
    }
    if (showKeyword2 && !keyword2.trim()) {
      toast.error(language === "tr" ? "2. keyword girin veya X ile kaldırın" : "Enter 2nd keyword or remove with X");
      return;
    }
    if (showKeyword3 && !keyword3.trim()) {
      toast.error(language === "tr" ? "3. keyword girin veya X ile kaldırın" : "Enter 3rd keyword or remove with X");
      return;
    }

    setBulkOrdering(true);

    const submitOrder = async (cartSnapshot) => {
      const kws = [keyword1.trim(), showKeyword2 ? keyword2.trim() : "", showKeyword3 ? keyword3.trim() : ""].filter(Boolean);
      const items = cartSnapshot.flatMap(item => kws.map(anchor_text => ({ site_id: item.site_id, anchor_text })));
      return api.post("/orders/bulk", {
        target_url: cleanTarget,
        items,
        hidden_link: Boolean(hiddenLink),
      });
    };

    try {
      toast.loading(language === "tr" ? "Siparişler oluşturuluyor..." : "Creating orders...", { id: "bulk-order" });

      let response;
      try {
        response = await submitOrder(cart);
      } catch (firstErr) {
        const detail = firstErr?.response?.data?.detail;
        const isStaleList = detail && typeof detail === "object" && Array.isArray(detail.removed_site_ids);
        if (isStaleList && detail.removed_site_ids.length) {
          // Backend bayat listeyi tespit etti: silinmis ID'leri sepetten cikar ve tek seferde retry et.
          removeMultipleFromCart(detail.removed_site_ids);
          const remaining = cart.filter(item => !detail.removed_site_ids.includes(item.site_id));
          if (!remaining.length) throw firstErr;
          toast.loading(
            language === "tr"
              ? `${detail.removed_site_ids.length} eski site otomatik atlandi, kalanlar siparis ediliyor...`
              : `${detail.removed_site_ids.length} stale sites dropped, retrying with remaining...`,
            { id: "bulk-order" }
          );
          response = await submitOrder(remaining);
        } else {
          throw firstErr;
        }
      }

      toast.dismiss("bulk-order");

      let result = response.data;
      const skippedHidden = result?.skipped_hidden_incompatible || 0;
      if (Array.isArray(result?.hidden_incompatible_site_ids) && result.hidden_incompatible_site_ids.length) {
        removeMultipleFromCart(result.hidden_incompatible_site_ids);
      }
      const queuedCount = result?.queued_count || result?.items_count || result?.orders_count || 0;
      if (result?.queued || result?.status === "queued" || result?.batch_id) {
        const skipNote = skippedHidden
          ? (language === "tr"
              ? ` (${skippedHidden} eski connector gizli link için atlandı)`
              : ` (${skippedHidden} old connectors skipped for hidden link)`)
          : "";
        toast.success(
          language === "tr"
            ? `${queuedCount} sipariş kuyruğa alındı! Arka planda işlenecek.${skipNote}`
            : `${queuedCount} orders queued! They will be processed in the background.${skipNote}`
        );
        clearCart();
        resetKeywords();
        setCartOpen(false);
        refreshUser();
        // Katalogda kal — Linklerim'e otomatik yönlendirme yok
        return;
      }
      const successCount = queuedCount;

      if (result.warning) {
        toast.warning(`${successCount} ${language === "tr" ? "sipariş oluşturuldu" : "orders created"}. ${result.warning}`);
      } else {
        toast.success(language === "tr" ? `${successCount} sipariş oluşturuldu!` : `${successCount} orders created!`);
      }

      clearCart();
      resetKeywords();
      setCartOpen(false);
      refreshUser();
      // Katalogda kal — Linklerim'e otomatik yönlendirme yok
    } catch (error) {
      toast.dismiss("bulk-order");
      const detail = error?.response?.data?.detail;
      const message = detail && typeof detail === "object" ? detail.message : detail;
      if (detail && typeof detail === "object" && Array.isArray(detail.removed_site_ids)) {
        removeMultipleFromCart(detail.removed_site_ids);
      }
      if (detail && typeof detail === "object" && Array.isArray(detail.hidden_incompatible_site_ids)) {
        removeMultipleFromCart(detail.hidden_incompatible_site_ids);
      }
      toast.error(message || (language === "tr" ? "Sipariş hatası" : "Order error"));
    } finally {
      setBulkOrdering(false);
    }
  };

  // Sepet boşsa gösterme
  if (cart.length === 0) return null;

  return (
    <>
      {/* Floating Cart Button - Telegram ikonunun solunda, aynı hizada (bottom) */}
      <button
        onClick={handleOpenCart}
        className="fixed bottom-6 right-[7rem] z-50 bg-primary text-black px-4 py-3 rounded-xl shadow-lg hover:scale-105 transition-all flex items-center gap-2"
      >
        <ShoppingCart className="w-5 h-5" />
        <span className="font-mono font-bold">{cart.length > 999 ? "999+" : cart.length}</span>
        <span className="text-xs">{language === "tr" ? "site" : "sites"}</span>
      </button>

      {/* Cart Dialog - Basitleştirilmiş */}
      <Dialog open={cartOpen} onOpenChange={setCartOpen}>
        <DialogContent className="bg-black border border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-mono flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 text-primary" />
              {language === "tr" ? "SEPET" : "CART"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Cart Summary - Basit Özet */}
            <div className="p-4 bg-primary/10 border border-primary/30 rounded-lg text-center">
              <div className="text-4xl font-mono font-bold text-primary mb-1">{cart.length}</div>
              <div className="text-sm text-muted-foreground font-mono">
                {language === "tr" ? "site seçildi" : "sites selected"}
              </div>
            </div>

            {/* Target URL */}
            <div>
              <label className="text-xs font-mono text-muted-foreground block mb-2">
                {language === "tr" ? "Hedef URL (Tüm siparişler için)" : "Target URL (For all orders)"}
              </label>
              <Input
                value={targetUrl}
                onChange={(e) => {
                  const v = sanitizeUrlInput(e.target.value);
                  setTargetUrl(v);
                  localStorage.setItem('rootseo_target_url', v);
                }}
                placeholder="https://yoursite.com/page"
                className="bg-black/50 border-white/10 font-mono"
              />
            </div>

            {/* Keyword zorunlu. + ile 2./3. keyword (açıksa doldurulması zorunlu, X ile kapatılabilir). */}
            <div className="space-y-2">
              <label className="text-xs font-mono text-muted-foreground block">
                {language === "tr" ? "Keyword (Tüm sitelere uygulanır) *" : "Keyword (Applied to all sites) *"}
              </label>
              <Input
                id="bulk-anchor-input"
                value={keyword1}
                onChange={(e) => setKeyword1(e.target.value)}
                placeholder={language === "tr" ? "Keyword 1" : "Keyword 1"}
                className="bg-black/50 border-white/10 font-mono"
              />
              {showKeyword2 && (
                <div className="flex gap-2 items-center">
                  <Input
                    value={keyword2}
                    onChange={(e) => setKeyword2(e.target.value)}
                    placeholder={language === "tr" ? "Keyword 2" : "Keyword 2"}
                    className="flex-1 bg-black/50 border-white/10 font-mono"
                  />
                  <button type="button" onClick={() => { setShowKeyword2(false); setKeyword2(""); }} className="text-red-400 hover:text-red-300 p-1.5 rounded">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
              {showKeyword3 && (
                <div className="flex gap-2 items-center">
                  <Input
                    value={keyword3}
                    onChange={(e) => setKeyword3(e.target.value)}
                    placeholder={language === "tr" ? "Keyword 3" : "Keyword 3"}
                    className="flex-1 bg-black/50 border-white/10 font-mono"
                  />
                  <button type="button" onClick={() => { setShowKeyword3(false); setKeyword3(""); }} className="text-red-400 hover:text-red-300 p-1.5 rounded">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
              {(!showKeyword2 || !showKeyword3) && (
                <button
                  type="button"
                  onClick={() => (showKeyword2 ? setShowKeyword3(true) : setShowKeyword2(true))}
                  className="flex items-center gap-1.5 text-xs text-primary hover:text-primary/80 font-mono"
                >
                  <Plus className="w-4 h-4" />
                  {language === "tr" ? (!showKeyword2 ? "2. keyword ekle" : "3. keyword ekle") : (!showKeyword2 ? "Add 2nd keyword" : "Add 3rd keyword")}
                </button>
              )}
            </div>

            {/* Gizli link: Ahrefs/Semrush vb. araçlardan gizle, Google görsün */}
            <label className="flex items-start gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={hiddenLink}
                onChange={(e) => setHiddenLink(e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-primary cursor-pointer"
              />
              <span className="text-sm font-mono text-foreground/90 flex items-center gap-1.5 flex-wrap">
                {language === "tr" ? "Gizli link" : "Hidden link"}
                <span className="relative inline-flex items-center group/tip">
                  <HelpCircle className="w-3.5 h-3.5 text-muted-foreground hover:text-primary" />
                  <span
                    role="tooltip"
                    className="pointer-events-none absolute left-1/2 bottom-full z-20 mb-2 w-64 -translate-x-1/2 rounded border border-white/15 bg-zinc-950 px-2.5 py-2 text-[11px] leading-snug text-muted-foreground opacity-0 shadow-lg transition-opacity group-hover/tip:opacity-100"
                  >
                    {language === "tr"
                      ? "Linklerinizi saldırılardan korunmak için Ahrefs, Semrush gibi yerlerden gizliyoruz. Google yine görür."
                      : "We hide your links from Ahrefs, Semrush and similar tools to protect against attacks. Google still sees them."}
                  </span>
                </span>
              </span>
            </label>

            <div className="text-sm text-muted-foreground font-mono text-center space-y-1">
              {hasActiveSubscription ? (
                <>
                  <div className="text-emerald-400 font-bold">
                    ∞ {language === "tr" ? "ABONELİK AKTİF" : "SUBSCRIPTION ACTIVE"}
                  </div>
                  {subscriptionEndLabel ? (
                    <div className="text-xs text-muted-foreground">
                      {language === "tr" ? "Bitiş: " : "Ends: "}
                      <span className="text-foreground/90">{subscriptionEndLabel}</span>
                      {" · "}
                      {language === "tr" ? "Kalan: " : "Left: "}
                      <span className="text-primary font-bold">
                        {subscriptionDaysLeft} {language === "tr" ? "gün" : "days"}
                      </span>
                    </div>
                  ) : null}
                </>
              ) : (
                <span className="text-amber-400 font-bold">
                  {language === "tr" ? "ABONELİK GEREKLİ" : "SUBSCRIPTION REQUIRED"}
                  {" · "}
                  <button type="button" className="underline hover:text-primary" onClick={() => { setCartOpen(false); navigate("/credits"); }}>
                    {language === "tr" ? "paket al" : "get plan"}
                  </button>
                </span>
              )}
            </div>

            {/* Actions - Her zaman görünür */}
            <div className="flex gap-3 pt-2">
              <Button 
                variant="outline" 
                onClick={() => {
                  // Always confirm before clearing cart (no limit check)
                  if (confirm(language === "tr" ? `${cart.length} siteyi silmek istediğinize emin misiniz?` : `Are you sure you want to clear ${cart.length} sites?`)) {
                    clearCart();
                    resetKeywords();
                    setCartOpen(false);
                  }
                }}
                className="flex-1 font-mono text-red-400 border-red-500/30 hover:bg-red-500/10"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                {language === "tr" ? "TEMİZLE" : "CLEAR"}
              </Button>
              <Button 
                onClick={handleBulkOrder}
                disabled={bulkOrdering || !hasActiveSubscription}
                className="flex-1 font-mono bg-primary text-black hover:bg-primary/90"
              >
                {bulkOrdering 
                  ? (language === "tr" ? "İŞLENİYOR..." : "PROCESSING...") 
                  : (language === "tr" ? "SİPARİŞ VER" : "PLACE ORDER")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FloatingCart;
